import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { FaCoins } from 'react-icons/fa';
import { getAuthHeaders } from '../utils/auth';
import { FOUNDER_EMAILS } from '../config';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Vibrant colors from RetroWordmark
const colors = [
  '#00FF9F', // UI Green
  '#FF1493', // Deep pink
  '#0066FF', // Bright blue
  '#FF6B00', // Orange
  '#FF00FF', // Hot pink
  '#FFB800', // Amber
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

const SubscriptionPlan = ({ 
  name, 
  price, 
  description, 
  isPopular, 
  onSubscribe, 
  isProcessing,
  isAuthenticated,
  isCurrent,
  backgroundImage,
  planComparison
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Random rotations that change on hover
  const [randomRotation] = useState(() => Math.random() * 4 - 2);
  const [bgRandomRotation] = useState(() => Math.random() * 6 - 3);
  const [hoverRotation, setHoverRotation] = useState(0);
  const [bgHoverRotation, setBgHoverRotation] = useState(0);

  // Generate new random rotations on hover
  useEffect(() => {
    if (isHovered) {
      setHoverRotation(Math.random() * 8 - 4); // -4 to +4 degrees
      setBgHoverRotation(Math.random() * 12 - 6); // -6 to +6 degrees
    }
  }, [isHovered]);

  // Use different colors for popular plans
  const bgColor = isPopular ? '#FF1493' : colors[Math.floor(Math.random() * colors.length)];

  return (
    <motion.div
      className="relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background card */}
      <motion.div
        className="absolute inset-0 rounded-lg shadow-lg"
        style={{
          background: bgColor,
          transformOrigin: 'center center',
          zIndex: 1
        }}
        initial={{ 
          rotate: bgRandomRotation,
          scale: 1.05,
          opacity: 0.8
        }}
        animate={{ 
          rotate: isHovered ? bgHoverRotation : bgRandomRotation,
          scale: isHovered ? 1.08 : 1.05,
          opacity: isHovered ? 0.9 : 0.8
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          mass: 1
        }}
      />

      {/* Main card */}
      <motion.div
        className="relative rounded-lg overflow-hidden"
        style={{
          zIndex: 2
        }}
        animate={{
          rotate: isHovered ? hoverRotation : randomRotation,
          scale: isHovered ? 1.02 : 1,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          mass: 1
        }}
      >
        {/* Background */}
        <div className="absolute inset-0">
          {backgroundImage ? (
            <img src={backgroundImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-black/40 to-black/20" />
          )}
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to top, 
                rgba(0, 0, 0, 0.75), 
                rgba(0, 0, 0, 0.5)
              )`
            }}
          />
        </div>

        {/* Content */}
        <div className="relative p-6 flex flex-col h-full">
          <div className="flex-grow">
            <h3 className="text-xl font-['VT323'] text-white mb-2">{name}</h3>
            <div className="text-2xl font-['VT323'] text-white mb-4">${price}/month</div>
            <div className="text-sm font-['VT323'] text-white/80 space-y-2">
              {description.split('•').map((feature, index) => (
                <div key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{feature.trim()}</span>
                </div>
              ))}
            </div>
          </div>

          {planComparison === 'current' ? (
            <div className="w-full py-3 px-4 font-['VT323'] text-lg mt-6 text-white text-center">
              ✓ Your Current Plan
            </div>
          ) : (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => isAuthenticated && planComparison !== 'current' ? onSubscribe() : !isAuthenticated ? toast.error('Please log in to subscribe') : null}
              disabled={isProcessing || !isAuthenticated}
              className={`w-full py-2 px-4 rounded-lg font-['VT323'] text-lg mt-6 transition-colors
                ${!isAuthenticated
                  ? 'bg-white/20 text-white/60 cursor-not-allowed'
                  : isProcessing
                    ? 'bg-white/20 text-white cursor-not-allowed'
                    : planComparison === 'upgrade'
                      ? 'bg-white hover:bg-gray-100 text-black'
                      : planComparison === 'downgrade'
                        ? 'bg-transparent hover:bg-white/10 text-white border-2 border-white/50'
                        : 'bg-white hover:bg-gray-100 text-black'
                }`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white/40 rounded-full animate-spin" />
                </div>
              ) : !isAuthenticated ? (
                'Login to Subscribe'
              ) : planComparison === 'upgrade' ? (
                'Upgrade'
              ) : planComparison === 'downgrade' ? (
                'Downgrade'
              ) : (
                'Subscribe'
              )}
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const TokenPurchase = ({ currentPlan, isLoading }) => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const [processingPlan, setProcessingPlan] = useState(null);
  const [founderImages, setFounderImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  const subscriptionPlans = [
    {
      name: 'Starter',
      price: 19,
      description: '150 images/month • Unlimited high-res storage • Keep your images forever',
      tier: 'starter'
    },
    {
      name: 'Bomber',
      price: 39,
      description: '500 images/month • Unlimited high-res storage • Keep your images forever • Priority support',
      tier: 'bomber'
    },
    {
      name: 'Vintage Pro',
      price: 69,
      description: '1500 images/month • Unlimited high-res storage • Keep your images forever • Priority support • Early access to new features',
      tier: 'vintage_pro'
    }
  ];

  useEffect(() => {
    const fetchFounderImages = async () => {
      try {
        setIsLoadingImages(true);
        
        // Try each founder in sequence until we get images
        for (const founder of FOUNDER_EMAILS) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/favorites/public/founder?founder=${encodeURIComponent(founder)}`);
            
            if (!response.ok) {
              console.warn(`Failed to fetch images for founder ${founder}`);
              continue;
            }
            
            const data = await response.json();
            if (data && data.length > 0) {
              // Shuffle the images and take a few for backgrounds
              const shuffled = [...data].sort(() => Math.random() - 0.5);
              setFounderImages(shuffled.slice(0, 3).map(img => ({
                ...img,
                url: img.thumbnailUrl || img.s3Url || img.url
              }))); // Get 3 images for backgrounds
              break; // Exit the loop once we have images
            }
          } catch (error) {
            console.warn(`Error fetching images for founder ${founder}:`, error);
          }
        }
      } catch (error) {
        console.error('Error fetching founder images:', error);
      } finally {
        setIsLoadingImages(false);
      }
    };

    fetchFounderImages();
  }, []);

  // Helper function to determine plan relationship
  const getPlanComparison = (planTier, currentPlan) => {
    const tierOrder = ['starter', 'bomber', 'vintage_pro'];
    const currentIndex = tierOrder.indexOf(currentPlan);
    const planIndex = tierOrder.indexOf(planTier);
    
    if (currentIndex === -1) {
      // User has no current plan, so everything is an upgrade
      return 'upgrade';
    }
    
    if (planIndex === currentIndex) {
      return 'current';
    } else if (planIndex > currentIndex) {
      return 'upgrade';
    } else {
      return 'downgrade';
    }
  };

  const handleSubscribe = async (plan) => {
    if (!isAuthenticated) {
      loginWithRedirect();
      return;
    }

    setProcessingPlan(plan.tier);
    try {
      const token = await getAccessTokenSilently();
      
      // Map frontend tier to backend planName
      const tierMapping = {
        'starter': 'Basic',
        'bomber': 'Pro', 
        'vintage_pro': 'Business'
      };
      
      const response = await fetch(`${API_URL}/api/tokens/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ planName: tierMapping[plan.tier] })
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error('Failed to start subscription. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-96 bg-black/5 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {subscriptionPlans.map((plan, index) => (
        <SubscriptionPlan
          key={plan.tier}
          {...plan}
          isProcessing={processingPlan === plan.tier}
          isAuthenticated={isAuthenticated}
          isCurrent={currentPlan === plan.tier}
          backgroundImage={founderImages[index]?.url}
          planComparison={getPlanComparison(plan.tier, currentPlan)}
          onSubscribe={() => handleSubscribe(plan)}
        />
      ))}
    </div>
  );
};

export default TokenPurchase; 
